import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import SettingsService from '../services/settingsService';
import { trackPageView } from '../services/analytics';
import useSystemSettings from '../hooks/useSystemSettings';
import { PAGE_SEO_ATTR } from '../hooks/usePageSeo';
import { absolutePublicAssetUrl, isIndiaSite } from '../data/seo/siteEntity';

interface MetaTagsManagerProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  customMeta?: Record<string, string>;
}

/**
 * MetaTagsManager applies *site-wide* meta defaults (from system settings)
 * exactly once after settings load, then keeps `<link rel="canonical">` and
 * `<meta property="og:url">` in sync with the current route on every
 * navigation.
 *
 * Prior versions also overwrote `<title>` / `<meta name="description">` on
 * every path change, which raced with per-page `useEffect`s in marketing
 * pages (ChatWidgetMarketingPage, IndustryUseCasePage, …) and silently wiped
 * out per-page SEO when the system settings query resolved last. Per-page
 * components now own their own title/description/og:image; this manager only
 * owns canonical, og:url, GTM, and the *initial* defaults from index.html.
 */
const MetaTagsManager: React.FC<MetaTagsManagerProps> = ({
  title,
  description,
  keywords,
  ogImage,
  customMeta = {},
}) => {
  const location = useLocation();
  const { settings, loading } = useSystemSettings();
  const hasAppliedDefaultsRef = useRef(false);
  const initialPageViewSkippedRef = useRef(false);

  // Effect 1: apply site-wide defaults from system settings ONCE after they
  // resolve. Per-page useEffects can still set their own title/description
  // afterwards and we will not overwrite them on subsequent renders.
  useEffect(() => {
    if (loading || settings.maintenanceMode) {
      return;
    }
    if (hasAppliedDefaultsRef.current) {
      return;
    }

    try {
      // If a per-page hook (usePageSeo) already applied title/description, do
      // NOT clobber it with the site-wide DB defaults. This matters most during
      // static prerender, where settings resolve AFTER the page's SEO ran.
      const pageOwnsSeo = document.documentElement.getAttribute(PAGE_SEO_ATTR) === 'true';
      const finalTitle = title || (pageOwnsSeo ? undefined : settings.metaTitle);
      const finalDescription = description || (pageOwnsSeo ? undefined : settings.metaDescription);
      const finalKeywords = keywords || (pageOwnsSeo ? undefined : settings.metaKeywords);
      const rawOgImage = ogImage || settings.ogImage;
      const finalOgImage = rawOgImage ? absolutePublicAssetUrl(String(rawOgImage)) : rawOgImage;

      if (finalTitle) {
        document.title = finalTitle;
        updateMetaProperty('og:title', finalTitle);
        updateMetaName('twitter:title', finalTitle);
      }
      if (finalDescription) {
        updateMetaTag('description', finalDescription);
        updateMetaProperty('og:description', finalDescription);
        updateMetaName('twitter:description', finalDescription);
      }
      if (finalKeywords) {
        updateMetaTag('keywords', finalKeywords);
      }
      if (finalOgImage) {
        updateMetaProperty('og:image', finalOgImage);
        updateMetaName('twitter:image', finalOgImage);
      }

      // Don't clobber a per-page og:type (blog posts and docs use `article`).
      // The shell and usePageSeo already set a type; this default only fills
      // the gap for routes that never opted into page SEO.
      if (!pageOwnsSeo) {
        updateMetaProperty('og:type', 'website');
      }
      updateMetaName('twitter:card', 'summary_large_image');

      Object.entries(customMeta).forEach(([name, content]) => {
        updateMetaTag(name, content);
      });

      hasAppliedDefaultsRef.current = true;
    } catch (error) {
      console.error('Failed to apply default meta tags:', error);
    }
  }, [loading, settings, title, description, keywords, ogImage, customMeta]);

  // Effect 2: keep canonical + og:url in sync with the *current path* on every
  // navigation. This is the only meta that should reactively change with the
  // route — title/description/og:image belong to the page component.
  useEffect(() => {
    if (loading || settings.maintenanceMode) {
      return;
    }
    try {
      // India ignores the DB appUrl, which is still flogpt.com on the copied
      // settings document. The default build keeps appUrl || window origin.
      const origin = isIndiaSite()
        ? 'https://flogpt.in'
        : String(settings.appUrl || window.location.origin).replace(/\/+$/, '');
      const path = canonicalisePath(location.pathname);
      const fullUrl = `${origin}${path}`;
      updateCanonicalUrl(fullUrl);
      updateMetaProperty('og:url', fullUrl);
    } catch (error) {
      console.error('Failed to update canonical URL:', error);
    }
  }, [loading, settings.appUrl, settings.maintenanceMode, location.pathname]);

  // Effect 3: SPA virtual pageviews. GTM's Google tag already counts the
  // initial document load, so the first route is skipped here and only
  // client-side navigations push `spa_pageview` to the dataLayer.
  useEffect(() => {
    if (!initialPageViewSkippedRef.current) {
      initialPageViewSkippedRef.current = true;
      return;
    }
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  // GTM scripts — load once after settings resolve. Re-run when the GTM ID
  // changes (admin can toggle from settings UI).
  useEffect(() => {
    const injectGTMScripts = async () => {
      try {
        await SettingsService.injectGTMScripts();
      } catch (error) {
        console.error('Failed to inject GTM scripts:', error);
      }
    };

    if (!loading && !settings.maintenanceMode) {
      injectGTMScripts();
    }
  }, [loading, settings.maintenanceMode, settings.enableGoogleTagManager, settings.googleTagManagerId]);

  return null;
};

/**
 * Normalise a pathname for canonical use:
 *   - keep `/` for the homepage
 *   - strip trailing slash on every other path so `/pricing/` and `/pricing`
 *     don't become two distinct canonicals
 *   - drop query strings and fragments (canonical never includes them unless
 *     the query is part of the resource identity, which we don't have today)
 */
const canonicalisePath = (pathname: string): string => {
  if (!pathname || pathname === '/') return '/';
  const noQuery = pathname.split('?')[0].split('#')[0];
  return noQuery.endsWith('/') ? noQuery.slice(0, -1) : noQuery;
};

const updateMetaTag = (name: string, content: string): void => {
  let metaTag = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
  if (!metaTag) {
    metaTag = document.createElement('meta');
    metaTag.name = name;
    document.head.appendChild(metaTag);
  }
  metaTag.content = content;
};

const updateMetaProperty = (property: string, content: string): void => {
  let metaTag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
  if (!metaTag) {
    metaTag = document.createElement('meta');
    metaTag.setAttribute('property', property);
    document.head.appendChild(metaTag);
  }
  metaTag.content = content;
};

const updateMetaName = (name: string, content: string): void => {
  let metaTag = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
  if (!metaTag) {
    metaTag = document.createElement('meta');
    metaTag.name = name;
    document.head.appendChild(metaTag);
  }
  metaTag.content = content;
};

const updateCanonicalUrl = (url: string): void => {
  let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
  if (!canonicalLink) {
    canonicalLink = document.createElement('link');
    canonicalLink.rel = 'canonical';
    document.head.appendChild(canonicalLink);
  }
  canonicalLink.href = url;
};

export default MetaTagsManager;
